import { useEffect, type ReactNode } from 'react';
import { signOut } from 'firebase/auth';
import { firebaseAuth } from '../../lib/firebase';
import { isAllowedEmail } from '../../lib/authDomain';
import { ApiError } from '../../lib/api';
import { DISABLED_ACCESS_MESSAGE } from '../../lib/userAccess';
import { CenteredSpinner } from '../../components/ui/Spinner';
import { Button } from '../../components/ui/button';
import { CurrentUserProvider } from './CurrentUserContext';
import { SignIn } from './SignIn';
import { useAuth } from './useAuth';
import { useCurrentUserQuery } from './useCurrentUser';

export function SignedInGuard({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const emailOk = !!user && isAllowedEmail(user.email);
  const me = useCurrentUserQuery(emailOk);

  if (loading) return <CenteredSpinner />;

  // ⚠️ A signed-in session whose email is not staff USED TO BE SIGNED OUT
  // AUTOMATICALLY here. That was harmless while only kyck people could sign in
  // at all; the Client Portal made it destructive. Firebase auth is one session
  // per browser, so a CLIENT who follows a stale bookmark to /accounts — or
  // mistypes a URL — would have had their PORTAL session silently ended,
  // landing them on the agency sign-in page with no idea what happened and no
  // way back except requesting a fresh email link.
  //
  // Now we render the choice instead of taking it. It is also better for the
  // original case: a staff member signed in with the wrong Google account is
  // told which account they are on, rather than being bounced with no
  // explanation. Nothing about the security posture changes — the session was
  // never what granted access; every API call is gated server-side.
  if (user && !emailOk) {
    return <WrongAccount email={user.email ?? 'this account'} />;
  }

  if (!user) {
    return <SignIn />;
  }

  if (me.isLoading) return <CenteredSpinner />;

  // An admin has turned this person's access off. Say so plainly (the server's
  // message is the same string) and end the session: their refresh tokens are
  // already revoked, so staying "signed in" would only look broken.
  if (me.error instanceof ApiError && me.error.status === 403 && me.error.message === DISABLED_ACCESS_MESSAGE) {
    return <AccessOff />;
  }

  if (me.error || !me.data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 max-w-sm w-full text-center">
          <h1 className="text-xl font-semibold mb-2 text-slate-900">Something went wrong</h1>
          <p className="text-slate-600 text-sm mb-6">
            We couldn't load your account. Try signing out and back in.
          </p>
          <Button
            variant="outline"
            onClick={() => void signOut(firebaseAuth)}
            className="w-full"
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return <CurrentUserProvider value={me.data}>{children}</CurrentUserProvider>;
}

/**
 * Shown when a Firebase session exists but the address is not a staff one.
 *
 * The two realistic arrivals are a CLIENT who followed a link into the agency
 * app, and a staff member signed in with a personal Google account. Both are
 * offered the route they actually want, and neither is signed out on their
 * behalf — the client would lose a portal session they are in the middle of.
 */
function WrongAccount({ email }: { email: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 max-w-sm w-full text-center">
        <h1 className="text-xl font-semibold mb-2 text-slate-900">
          This is the Kyck team app
        </h1>
        <p className="text-slate-600 text-sm mb-6">
          You're signed in as <span className="font-medium">{email}</span>, which doesn't have
          access here. If you're a Kyck client, your reports are in the client portal.
        </p>
        <div className="space-y-2">
          <Button asChild className="w-full">
            <a href="/portal">Go to the client portal</a>
          </Button>
          <Button
            variant="outline"
            onClick={() => void signOut(firebaseAuth)}
            className="w-full"
          >
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

function AccessOff() {
  useEffect(() => {
    void signOut(firebaseAuth);
  }, []);
  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white p-8 rounded-lg shadow-sm border border-slate-200 max-w-sm w-full text-center">
        <h1 className="text-xl font-semibold mb-2 text-slate-900">Access turned off</h1>
        <p className="text-slate-600 text-sm">{DISABLED_ACCESS_MESSAGE}</p>
      </div>
    </div>
  );
}
